import {
    DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import AddAdvancementpointDialog from '../dialogs/addAdvancementpointDialog';
import AddCertificationDialog from '../dialogs/addCertificateDialog';
import ChangePrizeDialog from '../dialogs/changePrizeDialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    Dialog,
} from "../ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';
import RemoveAdvancementpointDialog from '../dialogs/removeAdvancementpointDialog';
import RemoveCertificateDialog from '../dialogs/removeCertificateDialog';

export const columns = (auth, fetchClass, competitionClass) => {
    const isAdminOrPrivilegedUser = auth?.user?.superadmin || auth?.user?.bunnyadmin || auth?.user?.judge;

    const columnsArray = [
        {
            accessorKey: "place",
            header: ({ column }) => (
                <DatatableSortButton column={column}>Placering</DatatableSortButton>
            ),
            cell: info => {
                const value = info.getValue(); // Display the computed placement
                const row = info.row.original; // Access the row data for conditional checks

                // Check the conditions and render the badges accordingly
                return (
                    <div className='flex items-center'>
                        <span>{(value === null && !row.outOfCompetition && !row.crossedOut) ? "opl" : value}</span>
                        {row.crossedOut ? (
                            <Badge className='text-nowrap' variant='destructive'>Struken</Badge>
                        ) : row.outOfCompetition ? (
                            <Badge className='text-nowrap' variant='secondary'>Utom tävlan</Badge>
                        ) : row.only_cup ? (
                            <Badge className='text-nowrap' variant='outline'>Endast cup</Badge>
                        ) : null}
                    </div>
                );
            },
        },
        {
            accessorKey: "bunnyName",
            accessorFn: (row) => row.bunny?.name, // extract the nested name
            header: ({ column }) => <DatatableSortButton column={column}>Kanin</DatatableSortButton>,
            cell: info => (
                <Link
                    to={`/bunny/${info.row.original.bunny.id}`}
                    className='text-blue-600 font-medium hover:underline'
                >
                    {info.row.original.bunny.name}
                </Link>
            ),
        },
        {
            accessorKey: "driver_name",
            accessorFn: (row) => row.driver?.first_name, // extract the nested first name
            header: ({ column }) => <DatatableSortButton column={column}>Förare</DatatableSortButton>,
            cell: info => (
                <span>{info.row.original.driver.first_name} {info.row.original.driver.last_name}</span>
            ),
        },
        {
            accessorKey: "awards",
            header: ({ column }) => <DatatableSortButton column={column}>Utmärkelser</DatatableSortButton>,
            cell: ({ row }) => {
                const { award } = row.original;
                return (
                    <div className='flex gap-1'>
                        {award?.advancementPoint && <Badge>Pinne</Badge>}
                        {award?.certificate && <Badge>Certifikat</Badge>}
                        {award?.prize && <Badge>Pris</Badge>}
                    </div>
                );
            }
        },
    ];

    // Conditionally add the highestHeight column
    if ([13, 14, 16, 17].includes(competitionClass.sport_class_id)) {
        columnsArray.splice(3, 0, {
            accessorKey: "highestHeight",
            header: ({ column }) => (
                <DatatableSortButton column={column}>
                    {/* {([13, 14].includes(competitionClass.sport_class_id)) ? "Högsta höjd" : "Längsta längd"} */}
                    Bäst resultat
                </DatatableSortButton>
            ),
            cell: info => {
                const placing = info.row.getValue('place');
                const value = info.getValue();
                return placing === null ? "" : value ? `${value} cm` : "";
            },
        });
    } else {
        // Add totalTime and totalFaults columns
        columnsArray.splice(3, 0, {
            accessorKey: "totalTime",
            header: ({ column }) => <DatatableSortButton column={column}>Total tid</DatatableSortButton>,
            cell: info => {
                let placing = info.row.getValue('place');
                let value = info.getValue();
                if (value >= 1000) {
                    value = value - 1000;
                    value = value.toFixed(2);
                };

                const results = info.row.original.straightCrookedResults || [];
                const attempt = results.at(-1) || {};
                if (attempt.errors >= 99) {
                    return 'Ut';
                }
                return info.row.original.outOfCompetition
                    ? `${value} s`
                    : placing === null
                        ? ""
                        : value
                            ? `${value} s`
                            : "";
            },
        });
        columnsArray.splice(4, 0, {
            accessorKey: "totalFaults",
            header: ({ column }) => <DatatableSortButton column={column}>Antal fel</DatatableSortButton>,
            cell: info => {
                const placing = info.row.getValue('place');
                const value = info.getValue();

                const results = info.row.original.straightCrookedResults || [];
                const attempt = results.at(-1) || {};
                // Map errors from each round and join them with "+"
                const formattedErrors = results
                    .map(r => r.errors === 99 ? "Ut" : r.errors)
                    .join("+");

                return formattedErrors || "0";
                // return info.row.original.outOfCompetition
                //     ? value === 0
                //         ? "0"
                //         : value || "0"
                //     : placing === null
                //         ? ""
                //         : value === 0
                //             ? "0"
                //             : value || "0";
            },
        });
    }

    if (isAdminOrPrivilegedUser) {
        columnsArray.push(
            {
                id: "actions",
                enableHiding: false,
                cell: ({ row }) => {
                    const registration = row.original;
                    return (
                        <Dialog modal={true}>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0 float-right">
                                        <span className="sr-only">Öppna meny</span>
                                        <DotsHorizontalIcon className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onCloseAutoFocus={(e) => {
                                    e.preventDefault();
                                }}>
                                    <DropdownMenuLabel>Alternativ</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {!([17, 14, 11, 5].includes(competitionClass.sport_class_id)) ? (
                                        !registration.award?.advancementPoint ? (
                                            <AddAdvancementpointDialog
                                                fetchQualifications={fetchClass}
                                                bunny={registration.bunny}
                                                dropdown={true}
                                                competitionClass={competitionClass}
                                                registrationId={registration.registrationId}
                                            />
                                        ) : (
                                            auth?.user?.superadmin ? (
                                                <RemoveAdvancementpointDialog
                                                    fetchQualifications={fetchClass}
                                                    advancementpoint={registration.award?.advancementPoint}
                                                />
                                            ) : (
                                                <DropdownMenuItem className="text-gray-400">
                                                    Pinne redan tillagd
                                                </DropdownMenuItem>
                                            )
                                        )
                                    ) : null}

                                    {[17, 14, 11, 5].includes(competitionClass.sport_class_id) && (
                                        !registration.award?.certificate ? (
                                            <AddCertificationDialog
                                                fetchQualifications={fetchClass}
                                                bunny={registration.bunny}
                                                dropdown={true}
                                                competitionClass={competitionClass}
                                            />
                                        ) : (
                                            auth?.user?.superadmin ? (
                                                <RemoveCertificateDialog
                                                    fetchQualifications={fetchClass}
                                                    certificate={registration.award?.certificate}
                                                />
                                            ) : (
                                                <DropdownMenuItem className="text-gray-400">
                                                    Certifikat redan tillagt
                                                </DropdownMenuItem>
                                            )

                                        )
                                    )}
                                    <ChangePrizeDialog registration={registration} fetchQualifications={fetchClass} />
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </Dialog>
                    );
                },
            }
        );
    }

    return columnsArray;
};


export function CompetitionClassResultsDatatable({ competitionClass, fetchClass, isLoading, setColumnVisibility, columnVisibility, onColumnsReady }) {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const { auth } = useContext(AuthContext);

    // Map registrations to add place, highest height, total time, and total faults
    const dataWithPlaces = useMemo(() =>
        competitionClass.registrations
            .filter(registration => registration.crossedOut === null)
            .map((registration, index) => {
                if ([13, 14, 16, 17].includes(competitionClass.sport_class_id)) {
                    const successfulAttempts = registration.heightLengthResults?.filter(result => result.attempt1 || result.attempt2 || result.attempt3) || [];
                    const highestHeight = successfulAttempts.length > 0 ? Math.max(...successfulAttempts.map(result => parseFloat(result.height_length))) : null;
                    return {
                        ...registration,
                        place: registration.placing || null, // Add place based on placing value or null if no placing
                        highestHeight: highestHeight || "", // Add highest height or empty string if no height
                    };
                } else {
                    const totalTime = registration.straightCrookedResults?.reduce((sum, result) => sum + parseFloat(result.time), 0).toFixed(2) || "";
                    const totalFaults = registration.straightCrookedResults?.reduce((sum, result) => sum + result.errors, 0) || "";

                    return {
                        ...registration,
                        place: registration.placing || null, // Add place based on placing value or null if no placing
                        totalTime: totalTime, // Add total time or empty string if no time
                        totalFaults: totalFaults, // Add total faults or empty string if no faults
                    };
                }
            })
            .sort((a, b) => {
                if (a.place === null && b.place === null) {
                    return a?.bunny?.name?.localeCompare(b?.bunny?.name);
                } else if (a.place === null) {
                    return 1;
                } else if (b.place === null) {
                    return -1;
                } else if (a.place === b.place) {
                    return a?.bunny?.name?.localeCompare(b?.bunny?.name);
                } else {
                    return a.place - b.place;
                }
            }),
        [competitionClass.registrations, competitionClass.sport_class_id]
    );

    const columnsMemoized = useMemo(() => columns(auth, fetchClass, competitionClass), [auth, fetchClass, competitionClass]);

    useEffect(() => {
        if (onColumnsReady) {
            onColumnsReady(columnsMemoized);
        }
    }, [onColumnsReady]);

    const table = useReactTable({
        data: dataWithPlaces,
        columns: columnsMemoized,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            pagination: {
                pageIndex: 0,
                pageSize: 9999,
            },
        },
    });

    return (
        <DataTable
            table={table}
            isLoading={isLoading}
            columns={columnsMemoized}
        />
    );
}